import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  MessageSquare, Send, Users, Phone, CheckCircle, 
  XCircle, Clock, RefreshCw, Search, Filter,
  AlertCircle, Zap, FileText, TestTube
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Test phone number for all testing
const TEST_PHONE = '8173205334';

const SMSMarketing = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [sentMessages, setSentMessages] = useState([]);
  const [optedOutNumbers, setOptedOutNumbers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [activeTab, setActiveTab] = useState('compose');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Compose form
  const [composeForm, setComposeForm] = useState({
    recipient_type: 'individual', // individual, selected, all_subscribers
    phone_number: '',
    message: '',
    is_promotional: true
  });

  // Message templates
  const templates = [
    {
      id: 'promo_listing',
      name: 'Free Listing Promo',
      message: 'Its-Sold.com: List your home FREE this month! Save thousands on agent commissions. Visit its-sold.com to get started. Reply STOP to opt out.'
    },
    {
      id: 'featured_discount',
      name: 'Featured Listing Discount',
      message: 'Its-Sold.com: Get 50% OFF Featured Listing this week only! Your home at the top of search results. Visit its-sold.com/pricing. Reply STOP to opt out.'
    },
    {
      id: 'new_feature',
      name: 'New Feature Announcement',
      message: 'Its-Sold.com: NEW! AI-powered home valuation now available. Get your free estimate at its-sold.com. Reply STOP to opt out.'
    },
    {
      id: 'reminder',
      name: 'Listing Reminder',
      message: 'Its-Sold.com: Your listing is getting views! Log in to see your stats and respond to inquiries. Reply STOP to opt out.'
    }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, messagesRes, subscribersRes, optedOutRes] = await Promise.all([
        axios.get(`${API}/admin/sms/stats`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/admin/sms/messages`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/admin/sms/subscribers`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/admin/sms/opted-out`, { withCredentials: true }).catch(() => null)
      ]);

      if (statsRes?.data) setStats(statsRes.data);
      if (messagesRes?.data) setSentMessages(messagesRes.data.messages || []);
      if (subscribersRes?.data) setSubscribers(subscribersRes.data.subscribers || []);
      if (optedOutRes?.data) setOptedOutNumbers(optedOutRes.data.numbers || []);
    } catch (error) {
      console.error('Error fetching SMS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendTestMessage = async () => {
    if (!composeForm.message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/sms/send`, {
        recipients: [TEST_PHONE],
        message: `[TEST] ${composeForm.message}`,
        is_promotional: composeForm.is_promotional
      }, { withCredentials: true });

      if (response.data.successful_count > 0) {
        toast.success(`Test SMS sent to ${TEST_PHONE}!`);
        fetchData();
      } else {
        toast.error(`Failed to send test: ${response.data.failed?.[0]?.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setLoading(false);
    }
  };

  const sendIndividualSMS = async () => {
    if (!composeForm.phone_number.trim() || !composeForm.message.trim()) {
      toast.error('Please enter phone number and message');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/sms/send`, {
        recipients: [composeForm.phone_number],
        message: composeForm.message,
        is_promotional: composeForm.is_promotional
      }, { withCredentials: true });

      if (response.data.successful_count > 0) {
        toast.success('SMS sent successfully!');
        setComposeForm({ ...composeForm, phone_number: '', message: '' });
        fetchData();
      } else {
        toast.error(`Failed: ${response.data.failed?.[0]?.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const sendBulkSMS = async () => {
    if (!composeForm.message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    let recipients = [];
    
    if (composeForm.recipient_type === 'selected') {
      recipients = selectedRecipients;
      if (recipients.length === 0) {
        toast.error('Please select at least one recipient');
        return;
      }
    } else if (composeForm.recipient_type === 'all_subscribers') {
      recipients = subscribers.filter(s => s.sms_opt_in && s.phone).map(s => s.phone);
      if (recipients.length === 0) {
        toast.error('No opted-in subscribers with phone numbers');
        return;
      }
    }

    if (!confirm(`Send SMS to ${recipients.length} recipient(s)?\n\nMessage:\n${composeForm.message}`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/sms/send-bulk`, {
        recipients,
        message: composeForm.message,
        is_promotional: composeForm.is_promotional
      }, { withCredentials: true });

      toast.success(`Sent: ${response.data.successful_count} | Failed: ${response.data.failed_count}`);
      setSelectedRecipients([]);
      setComposeForm({ ...composeForm, message: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bulk SMS');
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template) => {
    setComposeForm({ ...composeForm, message: template.message });
    toast.success(`Template "${template.name}" loaded`);
  };

  const toggleRecipient = (phone) => {
    if (selectedRecipients.includes(phone)) {
      setSelectedRecipients(selectedRecipients.filter(p => p !== phone));
    } else {
      setSelectedRecipients([...selectedRecipients, phone]);
    }
  };

  const selectAllSubscribers = () => {
    // Select subscribers who opted into either transactional or promotional (or legacy sms_opt_in)
    const allPhones = subscribers.filter(s => 
      s.phone && (s.sms_opt_in || s.sms_opt_in_transactional || s.sms_opt_in_promotional)
    ).map(s => s.phone);
    setSelectedRecipients(allPhones);
  };

  const filteredSubscribers = subscribers.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.phone?.includes(searchTerm)
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Delivered</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Failed</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Pending</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">{status}</span>;
    }
  };

  if (loading && !stats) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading SMS Marketing...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 sm:w-7 h-6 sm:h-7 text-blue-600" />
            SMS Marketing Center
          </h2>
          <p className="text-gray-500 text-sm sm:text-base">Send individual or bulk SMS messages</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.total_subscribers || 0}</p>
              <p className="text-xs text-gray-500">SMS Subscribers</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Send className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.messages_sent_today || 0}</p>
              <p className="text-xs text-gray-500">Sent Today</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.messages_sent_month || 0}</p>
              <p className="text-xs text-gray-500">Sent This Month</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.opted_out_count || 0}</p>
              <p className="text-xs text-gray-500">Opted Out</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b">
        {[
          { id: 'compose', label: 'Compose', icon: Send },
          { id: 'templates', label: 'Templates', icon: FileText },
          { id: 'subscribers', label: 'Subscribers', icon: Users },
          { id: 'history', label: 'History', icon: Clock }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Compose Tab */}
      {activeTab === 'compose' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Compose Form */}
          <Card className="p-6">
            <h3 className="text-lg font-bold mb-4">Compose Message</h3>
            
            {/* Recipient Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Send To</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'individual', label: 'Individual' },
                  { id: 'selected', label: 'Selected Users' },
                  { id: 'all_subscribers', label: 'All Subscribers' }
                ].map(type => (
                  <button
                    key={type.id}
                    onClick={() => setComposeForm({ ...composeForm, recipient_type: type.id })}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      composeForm.recipient_type === type.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Individual Phone Input */}
            {composeForm.recipient_type === 'individual' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    placeholder="(214) 555-1234"
                    value={composeForm.phone_number}
                    onChange={(e) => setComposeForm({ ...composeForm, phone_number: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            )}

            {/* Selected Recipients Count */}
            {composeForm.recipient_type === 'selected' && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{selectedRecipients.length}</strong> recipient(s) selected
                  {selectedRecipients.length > 0 && (
                    <button 
                      onClick={() => setSelectedRecipients([])}
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </p>
              </div>
            )}

            {/* All Subscribers Count */}
            {composeForm.recipient_type === 'all_subscribers' && (
              <div className="mb-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  Will send to <strong>{subscribers.filter(s => s.sms_opt_in && s.phone).length}</strong> opted-in subscribers
                </p>
              </div>
            )}

            {/* Message */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <textarea
                placeholder="Type your message... (160 chars = 1 SMS)"
                value={composeForm.message}
                onChange={(e) => setComposeForm({ ...composeForm, message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
              />
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>{composeForm.message.length} characters</span>
                <span>{Math.ceil(composeForm.message.length / 160) || 1} SMS segment(s)</span>
              </div>
            </div>

            {/* Message Type */}
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={composeForm.is_promotional}
                  onChange={(e) => setComposeForm({ ...composeForm, is_promotional: e.target.checked })}
                  className="h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Promotional message (includes offers/discounts)</span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={sendTestMessage}
                disabled={loading || !composeForm.message.trim()}
                variant="outline"
                className="flex-1"
              >
                <TestTube className="w-4 h-4 mr-2" />
                Test to {TEST_PHONE}
              </Button>
              
              {composeForm.recipient_type === 'individual' ? (
                <Button
                  onClick={sendIndividualSMS}
                  disabled={loading || !composeForm.phone_number.trim() || !composeForm.message.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send SMS
                </Button>
              ) : (
                <Button
                  onClick={sendBulkSMS}
                  disabled={loading || !composeForm.message.trim()}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Send Bulk SMS
                </Button>
              )}
            </div>
          </Card>

          {/* Right: Quick Templates */}
          <Card className="p-6">
            <h3 className="text-lg font-bold mb-4">Quick Templates</h3>
            <div className="space-y-3">
              {templates.map(template => (
                <div 
                  key={template.id}
                  className="p-3 border rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => applyTemplate(template)}
                >
                  <p className="font-medium text-gray-900">{template.name}</p>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{template.message}</p>
                </div>
              ))}
            </div>

            {/* Test Info */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Testing Mode</p>
                  <p className="text-sm text-amber-800">
                    All test messages will be sent to: <strong>{TEST_PHONE}</strong>
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4">Message Templates</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {templates.map(template => (
              <div key={template.id} className="p-4 border rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-gray-900">{template.name}</h4>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      applyTemplate(template);
                      setActiveTab('compose');
                    }}
                  >
                    Use
                  </Button>
                </div>
                <p className="text-sm text-gray-600">{template.message}</p>
                <p className="text-xs text-gray-400 mt-2">{template.message.length} chars</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Subscribers Tab */}
      {activeTab === 'subscribers' && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">SMS Subscribers</h3>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <Button variant="outline" onClick={selectAllSubscribers}>
                Select All Opted-In
              </Button>
            </div>
          </div>

          {selectedRecipients.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg flex justify-between items-center">
              <span className="text-blue-800">
                <strong>{selectedRecipients.length}</strong> selected
              </span>
              <Button
                size="sm"
                onClick={() => {
                  setComposeForm({ ...composeForm, recipient_type: 'selected' });
                  setActiveTab('compose');
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Compose Message
              </Button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                    <input
                      type="checkbox"
                      checked={selectedRecipients.length === filteredSubscribers.filter(s => s.phone && (s.sms_opt_in || s.sms_opt_in_transactional || s.sms_opt_in_promotional)).length && selectedRecipients.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAllSubscribers();
                        } else {
                          setSelectedRecipients([]);
                        }
                      }}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opt-In Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSubscribers.map((subscriber, idx) => (
                  <tr key={idx} className={`hover:bg-gray-50 ${selectedRecipients.includes(subscriber.phone) ? 'bg-blue-50' : ''}`}>
                    <td className="px-4 py-3">
                      {subscriber.phone && (subscriber.sms_opt_in || subscriber.sms_opt_in_transactional || subscriber.sms_opt_in_promotional) ? (
                        <input
                          type="checkbox"
                          checked={selectedRecipients.includes(subscriber.phone)}
                          onChange={() => toggleRecipient(subscriber.phone)}
                          className="h-4 w-4"
                        />
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{subscriber.name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{subscriber.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{subscriber.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {(subscriber.sms_opt_in_transactional || subscriber.sms_opt_in) && (
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Trans</span>
                        )}
                        {subscriber.sms_opt_in_promotional && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Promo</span>
                        )}
                        {!subscriber.sms_opt_in && !subscriber.sms_opt_in_transactional && !subscriber.sms_opt_in_promotional && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">No</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredSubscribers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No subscribers found
              </div>
            )}
          </div>
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4">Message History</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sentMessages.map((msg, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(msg.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{msg.to_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{msg.message_text}</td>
                    <td className="px-4 py-3">{getStatusBadge(msg.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sentMessages.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No messages sent yet
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default SMSMarketing;
